import { useState } from 'react'

interface Props {
  onUseAmount: (amount: number) => void
  onClose: () => void
}

export default function POSCalculator({ onUseAmount, onClose }: Props) {
  const [expression, setExpression] = useState('')
  const [display, setDisplay] = useState('0')
  const [justCalculated, setJustCalculated] = useState(false)

  const handleNumber = (num: string) => {
    if (justCalculated) {
      setDisplay(num)
      setExpression(num)
      setJustCalculated(false)
      return
    }
    if (display === '0' && num !== '.') {
      setDisplay(num)
      setExpression(prev => prev.replace(/[\d.]*$/, num))
    } else {
      if (num === '.' && display.includes('.')) return
      setDisplay(prev => prev + num)
      setExpression(prev => prev + num)
    }
  }

  const handleOperator = (op: string) => {
    setJustCalculated(false)
    const lastChar = expression.slice(-1)
    const isOperator = ['+', '-', '×', '÷'].includes(lastChar)
    if (isOperator) {
      setExpression(prev => prev.slice(0, -2) + ' ' + op + ' ')
    } else {
      setExpression(prev => prev + ' ' + op + ' ')
    }
    setDisplay('0')
  }

  const handleEquals = () => {
    try {
      const evalExpr = expression
        .replace(/×/g, '*')
        .replace(/÷/g, '/')
      // eslint-disable-next-line no-new-func
      const result = Function('"use strict"; return (' + evalExpr + ')')()
      const rounded = Math.round(result * 100) / 100
      setDisplay(rounded.toString())
      setExpression(expression + ' = ' + rounded.toString())
      setJustCalculated(true)
    } catch {
      setDisplay('Error')
      setExpression('')
    }
  }

  const handleClear = () => {
    setDisplay('0')
    setExpression('')
    setJustCalculated(false)
  }

  const handleBackspace = () => {
    if (justCalculated) { handleClear(); return }
    if (display.length <= 1) {
      setDisplay('0')
    } else {
      setDisplay(prev => prev.slice(0, -1))
      setExpression(prev => prev.slice(0, -1))
    }
  }

  const handlePercent = () => {
    const val = parseFloat(display) / 100
    setDisplay(val.toString())
    setExpression(prev => prev.replace(/[\d.]+$/, val.toString()))
  }

  const handlePlusMinus = () => {
    const val = parseFloat(display) * -1
    setDisplay(val.toString())
    setExpression(prev => prev.replace(/[-\d.]+$/, val.toString()))
  }

  const displayVal = parseFloat(display)
  const canUse = !isNaN(displayVal) && displayVal > 0 && display !== 'Error'

  type Variant = 'number' | 'operator' | 'equals' | 'function'

  function Btn({
    label, onClick, variant = 'number', wide = false,
  }: { label: string; onClick: () => void; variant?: Variant; wide?: boolean }) {
    return (
      <button
        onClick={onClick}
        className={[
          'flex items-center justify-center rounded-2xl text-xl font-semibold',
          'transition-all active:scale-95 h-16',
          wide ? 'col-span-2' : '',
          variant === 'number'
            ? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
            : variant === 'operator'
              ? 'bg-orange-100 text-orange-600 hover:bg-orange-200'
              : variant === 'equals'
                ? 'bg-orange-500 text-white hover:bg-orange-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300',
        ].filter(Boolean).join(' ')}
      >
        {label}
      </button>
    )
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-3xl shadow-2xl w-80 overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Display */}
        <div className="bg-gray-900 px-6 pt-8 pb-4">
          <div className="text-gray-400 text-sm text-right min-h-[20px] font-mono mb-1 truncate">
            {expression || '\u00a0'}
          </div>
          <div className="text-white text-right font-light overflow-hidden">
            <span className={
              display.length > 10 ? 'text-3xl' :
              display.length > 7  ? 'text-4xl' :
              'text-5xl'
            }>
              {display}
            </span>
          </div>
        </div>

        {/* Buttons */}
        <div className="p-4 grid grid-cols-4 gap-3">
          <Btn label="C"   onClick={handleClear}                        variant="function" />
          <Btn label="+/-" onClick={handlePlusMinus}                    variant="function" />
          <Btn label="%"   onClick={handlePercent}                      variant="function" />
          <Btn label="÷"   onClick={() => handleOperator('÷')}          variant="operator" />

          <Btn label="7"   onClick={() => handleNumber('7')} />
          <Btn label="8"   onClick={() => handleNumber('8')} />
          <Btn label="9"   onClick={() => handleNumber('9')} />
          <Btn label="×"   onClick={() => handleOperator('×')}          variant="operator" />

          <Btn label="4"   onClick={() => handleNumber('4')} />
          <Btn label="5"   onClick={() => handleNumber('5')} />
          <Btn label="6"   onClick={() => handleNumber('6')} />
          <Btn label="−"   onClick={() => handleOperator('-')}          variant="operator" />

          <Btn label="1"   onClick={() => handleNumber('1')} />
          <Btn label="2"   onClick={() => handleNumber('2')} />
          <Btn label="3"   onClick={() => handleNumber('3')} />
          <Btn label="+"   onClick={() => handleOperator('+')}          variant="operator" />

          <Btn label="⌫"   onClick={handleBackspace}                    variant="function" />
          <Btn label="0"   onClick={() => handleNumber('0')} />
          <Btn label="."   onClick={() => handleNumber('.')} />
          <Btn label="="   onClick={handleEquals}                       variant="equals" />
        </div>

        {/* Use Amount */}
        <div className="px-4 pb-4">
          <button
            disabled={!canUse}
            onClick={() => {
              if (canUse) {
                onUseAmount(displayVal)
                onClose()
              }
            }}
            className="w-full py-4 bg-gray-900 text-white rounded-2xl text-base font-semibold
              hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {canUse
              ? `Use ₦${displayVal.toLocaleString('en-NG', { minimumFractionDigits: 2 })} as Payment`
              : 'Enter an amount'}
          </button>
        </div>
      </div>
    </div>
  )
}
